import { useContext, useMemo, useState } from "react"

import { Trans, useTranslation } from "react-i18next"
import { useQuery } from "react-query"
import { useParams } from "react-router-dom"

import { Grid } from "@mui/material"
import type { AxiosError, AxiosResponse } from "axios"
import { isEmpty as _isEmpty } from "lodash"

import { TopFeaturesGraph } from "../../../components/graphs/Bar/TopFeaturesGraph"
import { KonanAPI } from "../../../services/KonanAPI"
import type { FeaturesCountsInsight } from "../../../types/generated/api/FeaturesCountsInsight"
import { InsightList } from "../../../types/generated/api/InsightList"
import { ReportContext } from "../Context"
import type { InsightProps } from "../interfaces"

/**
 * TopFeaturesGraphContainer Component
 *
 * Displays the most influential features in the decision-making process, allowing
 * users to analyze feature importance across different labels.
 *
 * Features:
 * - Interactive label selection for filtered analysis
 * - Dynamic data processing for visualization
 * - Responsive empty and loading states
 * - Automatic data updates based on selection changes
 *
 * @param {InsightProps} insight - Insight metadata containing:
 *   - insight: Insight metadata including:
 *     - uuid: Unique identifier for the insight
 *     - status: Current status of the insight (e.g., SUCCEEDED, FAILED)
 * @returns {React.ReactElement} A Grid item with TopFeaturesGraph and selection controls
 */
export const TopFeaturesGraphContainer = (props: InsightProps): React.ReactElement => {
  const { insight } = props
  const { id: projectId } = useParams<{ id: string }>()

  const { report } = useContext(ReportContext)

  const { t } = useTranslation("insights")

  const [selectedLabel, setSelectedLabel] = useState<string | null>(null)

  // Fetch features counts insight data from the API
  const { data: featuresCountsInsight, isLoading: isLoadingFeaturesCountsInsight } = useQuery<
    AxiosResponse<FeaturesCountsInsight>,
    AxiosError
  >(
    ["report-top-features", projectId, report?.uuid, insight.uuid, selectedLabel],
    () =>
      KonanAPI.fetchFeaturesCountInsight({
        project_uuid: projectId as string,
        report_uuid: report?.uuid as string,
        insight_uuid: insight.uuid,
        labels: selectedLabel ?? undefined,
      }),
    {
      enabled: !!report?.uuid && !!projectId && !!insight.uuid && insight.status === InsightList.status.SUCCEEDED,
      refetchOnMount: false,
    },
  )

  const isEmpty = _isEmpty(featuresCountsInsight?.data.features_counts_per_label)

  // Process the fetched data to create top features data
  const topFeaturesData = useMemo(() => {
    const features: Array<{ name: string; values: Record<number | string, number> }> = []

    if (!isLoadingFeaturesCountsInsight && !!featuresCountsInsight?.data.features_counts_per_label)
      Object.keys(featuresCountsInsight?.data.features_counts_per_label).forEach((label) => {
        Object.entries(featuresCountsInsight?.data.features_counts_per_label?.[label] ?? {}).forEach(([key, value]) => {
          const topFeaturesPerLabel = { name: key, values: {} as Record<number | string, number> }

          // Convert to array of entries, sort by value, and take top 5
          const sortedEntries = Object.entries(value)
            .sort(([, a], [, b]) => (b as number) - (a as number))
            .slice(0, 5)

          // Convert back to object with formatted keys
          sortedEntries.forEach(([item, count]) => {
            topFeaturesPerLabel.values[`${item.replace("__", "->")}`] = count as number
          })

          features.push(topFeaturesPerLabel)
        })
      })

    return [{ label: selectedLabel ?? t("TopFeaturesInsights.filters.decisions.all"), features: features }]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featuresCountsInsight?.data.features_counts_per_label, isLoadingFeaturesCountsInsight])

  return (
    <Grid item xs={12}>
      <TopFeaturesGraph
        title={t("TopFeaturesInsights.title")}
        tooltipText={<Trans>{t("TopFeaturesInsights.tooltip")}</Trans>}
        graphHeight={340}
        showAxis={false}
        visible
        isLoading={isLoadingFeaturesCountsInsight}
        data={topFeaturesData ?? []}
        selectOptions={{
          options: [
            t("TopFeaturesInsights.filters.decisions.all"),
            ...(report?.labels?.map((label) => label?.label).filter((label) => label !== "NA") ?? []),
          ],
          value: selectedLabel ?? t("TopFeaturesInsights.filters.decisions.all"),
          setValue: (label) =>
            setSelectedLabel(label === t("TopFeaturesInsights.filters.decisions.all") ? null : label),
        }}
        emptyState={
          insight.status !== InsightList.status.SUCCEEDED
            ? {
                title: `${t("label.insight")} ${t(`status.${insight.status?.toLocaleLowerCase()}`, {
                  defaultValue: insight.status,
                })?.toLocaleLowerCase()}`,
              }
            : isEmpty
              ? { title: t("TopFeaturesInsights.unavailable") }
              : undefined
        }
      />
    </Grid>
  )
}
